import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DynamicForm from '../DynamicForm';
import LiquidEther from '../LiquidEther';
import Marquee from 'react-fast-marquee';
import GlitchText from '../GlitchText';
import CountUp from '../CountUp';
import GradientText from '../GradientText';
import { BACKEND_URL, api } from '../../lib/api';

const DEFAULT_CARD_IMAGES = [
  'https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=1200&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?q=80&w=1200&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1200&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=1200&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1529070538774-1843cb3265df?q=80&w=1200&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop',
];

const DEFAULT_GALLERY_IMAGE = 'https://images.unsplash.com/photo-1497366412874-3415097a27e7?q=80&w=1200&auto=format&fit=crop';

export function getBlockValue(block, key, lang) {
  if (lang === 'en' && block[`${key}_en`]) {
    return block[`${key}_en`];
  }
  return block[key];
}

export function normalizeTitle(value) {
  return (value || '').toString().trim().toLowerCase();
}

function isFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function buildResponsiveFontSize(mobileValue, desktopValue, fallbackMobile, fallbackDesktop) {
  const mobile = isFiniteNumber(mobileValue) ? Number(mobileValue) : fallbackMobile;
  const desktop = isFiniteNumber(desktopValue) ? Number(desktopValue) : fallbackDesktop;
  const fluid = ((mobile + desktop) / 2).toFixed(2);
  return `clamp(${mobile}rem, ${fluid}rem + 2vw, ${desktop}rem)`;
}

function getFontWeight(value, fallback) {
  return isFiniteNumber(value) ? Number(value) : fallback;
}

function getFallbackCardImage(index) {
  return DEFAULT_CARD_IMAGES[index % DEFAULT_CARD_IMAGES.length];
}

function setImageFallback(event, fallbackSrc = DEFAULT_GALLERY_IMAGE) {
  if (!event?.currentTarget) return;
  const img = event.currentTarget;
  if (img.dataset.fallbackApplied === 'true') return;
  img.dataset.fallbackApplied = 'true';
  img.src = fallbackSrc;
}

function hideBrokenImage(event) {
  if (!event?.currentTarget) return;
  event.currentTarget.style.display = 'none';
}

function getHeroTitleMetrics(title, isCompact) {
  const normalized = (title || '').trim();
  const length = normalized.length;
  const words = normalized.split(/\s+/).filter(Boolean).length;
  const isVeryLong = length >= 36 || words >= 6;
  const isLong = length >= 24 || words >= 4;

  if (isCompact) {
    if (isVeryLong) {
      return {
        mobile: 1.9,
        desktop: 4.8,
        widthCh: 18,
        lineHeight: 1.02,
      };
    }
    if (isLong) {
      return {
        mobile: 2.15,
        desktop: 5.6,
        widthCh: 16,
        lineHeight: 1,
      };
    }
    return {
      mobile: 2.4,
      desktop: 6.2,
      widthCh: 14,
      lineHeight: 0.98,
    };
  }

  if (isVeryLong) {
    return {
      mobile: 2.5,
      desktop: 6.5,
      widthCh: 17,
      lineHeight: 0.96,
    };
  }
  if (isLong) {
    return {
      mobile: 2.8,
      desktop: 7.5,
      widthCh: 15,
      lineHeight: 0.95,
    };
  }
  return {
    mobile: 3,
    desktop: 8,
    widthCh: 14,
    lineHeight: 0.95,
  };
}

export function BlockRenderer({ block, lang }) {
  switch (block.type) {
    case 'hero':
      return <HeroBlock block={block} lang={lang} useGlitch={block.glitch_enabled === true} />;
    case 'text':
      return <TextBlock block={block} lang={lang} />;
    case 'image':
      return <ImageBlock block={block} lang={lang} />;
    case 'gallery':
      return <GalleryBlock block={block} />;
    case 'video':
      return <VideoBlock block={block} lang={lang} />;
    case 'form':
      return <FormBlock block={block} lang={lang} />;
    case 'cards':
      return <CardsBlock block={block} lang={lang} />;
    case 'stats':
      return <StatsBlock block={block} lang={lang} />;
    case 'logo_grid':
      return <LogoGridBlock block={block} />;
    case 'cta':
      return <CtaBlock block={block} lang={lang} />;
    case 'list':
      return <ListBlock block={block} lang={lang} />;
    case 'collection':
      return <CollectionBlock block={block} lang={lang} />;
    case 'html':
      return <HtmlBlock block={block} lang={lang} />;
    case 'marquee':
      return <MarqueeBlock block={block} lang={lang} />;
    case 'spacer':
      return <SpacerBlock block={block} />;
    default:
      return null;
  }
}

export function HeroBlock({ block, lang, bottomSlot, useGlitch = false }) {
  const title = getBlockValue(block, 'title', lang);
  const subtitle = getBlockValue(block, 'subtitle', lang);
  const ctaLabel = getBlockValue(block, 'cta_label', lang);
  const backgroundImage = block.background_image;
  const backgroundVideo = block.background_video;
  const useLiquid = Boolean(block.use_liquid);
  const liquidColors = Array.isArray(block.liquid_colors) && block.liquid_colors.length
    ? block.liquid_colors
    : ['#e70d0d', '#850000', '#ea6d57'];
  const liquidSettings = block.liquid_settings || {};
  const isFullBleed = Boolean(block.full_bleed);
  const ctaIsExternal = block.cta_href?.startsWith('http');
  const hasBottomSlot = Boolean(bottomSlot);
  const isCenteredLayout = isFullBleed || block.align === 'center' || block.text_align === 'center';
  const sectionClass = isFullBleed
    ? `relative overflow-hidden flex ${hasBottomSlot ? 'flex-col' : 'items-center justify-center'}`
    : 'relative overflow-hidden rounded-3xl border border-white/10 bg-black/40 pt-20 sm:pt-24';
  const contentWrapperClass = isFullBleed
    ? `relative z-10 ${hasBottomSlot ? 'flex-1 w-full flex items-center justify-center' : ''}`
    : 'relative z-10';
  const isCompact = block.compact === true;
  const titleMetrics = getHeroTitleMetrics(title, isCompact);
  const contentInnerClass = isFullBleed
    ? 'max-w-[900px] mx-auto px-4 md:px-8 text-center space-y-5 pt-20 sm:pt-16'
    : isCompact
      ? 'p-8 md:p-12 space-y-4 max-w-4xl'
      : 'p-10 md:p-16 space-y-6';
  const glitchEnabled = useGlitch;
  const glitchSpeed = Number(block.glitch_speed ?? 1);
  const glitchShadows = block.glitch_shadows !== false;
  const glitchOnHover = block.glitch_on_hover === true;
  const titleFontSize = buildResponsiveFontSize(
    block.title_size_mobile,
    block.title_size_desktop,
    titleMetrics.mobile,
    titleMetrics.desktop,
  );
  const subtitleFontSize = buildResponsiveFontSize(
    block.subtitle_size_mobile,
    block.subtitle_size_desktop,
    lang === 'ru' ? (isCompact ? 0.95 : 0.95) : (isCompact ? 1 : 1),
    lang === 'ru' ? (isCompact ? 1.125 : 1.25) : (isCompact ? 1.25 : 1.5),
  );
  const titleTypographyStyle = {
    fontSize: `calc(${titleFontSize} * var(--hero-title-scale, 1))`,
    fontStyle: block.title_font_style || 'normal',
    fontWeight: getFontWeight(block.title_font_weight, 900),
    textTransform: block.title_text_transform || 'none',
    wordBreak: 'keep-all',
    overflowWrap: 'normal',
    hyphens: 'none',
    textWrap: 'balance',
    width: `min(100%, ${block.title_max_width_ch || titleMetrics.widthCh}ch)`,
    marginLeft: isCenteredLayout ? 'auto' : '0',
    marginRight: isCenteredLayout ? 'auto' : '0',
    textAlign: isCenteredLayout ? 'center' : 'left',
    lineHeight: block.title_line_height || titleMetrics.lineHeight,
    color: 'var(--site-heading-color, #FFFFFF)',
  };
  const subtitleTypographyStyle = {
    fontSize: `calc(${subtitleFontSize} * var(--body-text-scale, 1))`,
    fontStyle: block.subtitle_font_style || 'normal',
    fontWeight: getFontWeight(block.subtitle_font_weight, 400),
    textTransform: block.subtitle_text_transform || 'none',
    textAlign: isCenteredLayout ? 'center' : 'left',
    color: 'var(--site-body-color, rgba(255,255,255,0.78))',
  };
  const glitchStyle = glitchEnabled
    ? {
      '--glitch-size': titleFontSize,
      '--glitch-whitespace': 'normal',
      fontStyle: titleTypographyStyle.fontStyle,
      fontWeight: titleTypographyStyle.fontWeight,
      textTransform: titleTypographyStyle.textTransform,
      wordBreak: 'keep-all',
      overflowWrap: 'normal',
      hyphens: 'none',
      textWrap: 'balance',
      width: `min(100%, ${block.title_max_width_ch || titleMetrics.widthCh}ch)`,
      marginLeft: isCenteredLayout ? 'auto' : '0',
      marginRight: isCenteredLayout ? 'auto' : '0',
      textAlign: isCenteredLayout ? 'center' : 'left',
      lineHeight: block.title_line_height || titleMetrics.lineHeight,
      color: 'var(--site-heading-color, #FFFFFF)',
    }
    : undefined;

  return (
    <section className={sectionClass} style={isFullBleed ? { minHeight: 'calc(100svh / var(--site-zoom, 1))' } : undefined}>
      {useLiquid && (
        <div className="absolute inset-0 z-0">
          <LiquidEther
            colors={liquidColors}
            mouseForce={liquidSettings.mouseForce ?? 20}
            cursorSize={liquidSettings.cursorSize ?? 100}
            resolution={liquidSettings.resolution ?? 0.5}
            dt={liquidSettings.dt ?? 0.014}
            BFECC={liquidSettings.BFECC ?? true}
            isViscous={liquidSettings.isViscous ?? false}
            viscous={liquidSettings.viscous ?? 30}
            iterationsViscous={liquidSettings.iterationsViscous ?? 32}
            iterationsPoisson={liquidSettings.iterationsPoisson ?? 32}
            isBounce={liquidSettings.isBounce ?? false}
            autoDemo={liquidSettings.autoDemo ?? true}
            autoSpeed={liquidSettings.autoSpeed ?? 0.5}
            autoIntensity={liquidSettings.autoIntensity ?? 2.2}
            takeoverDuration={liquidSettings.takeoverDuration ?? 0.25}
            autoResumeDelay={liquidSettings.autoResumeDelay ?? 1000}
            autoRampDuration={liquidSettings.autoRampDuration ?? 0.6}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      )}
      {(backgroundImage || backgroundVideo) && (
        <div className="absolute inset-0">
          {backgroundVideo ? <video className="w-full h-full object-cover opacity-50" src={backgroundVideo} autoPlay muted loop /> : <img className="w-full h-full object-cover opacity-40" src={backgroundImage} alt="" />}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/70" />
      <div className={contentWrapperClass}>
        <div className={contentInnerClass}>
          {title && (
            glitchEnabled ? (
              <GlitchText
                as="h2"
                className="font-heading text-white leading-[0.95]"
                style={glitchStyle}
                enableOnHover={glitchOnHover}
                speed={Number.isFinite(glitchSpeed) ? glitchSpeed : 1}
                enableShadows={glitchShadows}
              >
                {title}
              </GlitchText>
            ) : (
              <h2 className="font-heading text-white" style={titleTypographyStyle}>
                {title}
              </h2>
            )
          )}
          {subtitle && <p style={subtitleTypographyStyle}>{subtitle}</p>}
          {ctaLabel && block.cta_href && (ctaIsExternal ? <a href={block.cta_href} className="inline-flex items-center px-6 py-3" style={{ background: 'var(--site-button-bg, #E11D2E)', color: 'var(--site-button-text, #FFFFFF)', borderRadius: 'var(--site-button-radius, 9999px)', fontSize: 'var(--site-button-text-size, 0.95rem)' }}>{ctaLabel}</a> : <Link to={block.cta_href} className="inline-flex items-center px-6 py-3" style={{ background: 'var(--site-button-bg, #E11D2E)', color: 'var(--site-button-text, #FFFFFF)', borderRadius: 'var(--site-button-radius, 9999px)', fontSize: 'var(--site-button-text-size, 0.95rem)' }}>{ctaLabel}</Link>)}
        </div>
      </div>
      {hasBottomSlot && isFullBleed && <div className="relative z-20 w-full">{bottomSlot}</div>}
    </section>
  );
}

export function TextBlock({ block, lang }) {
  const heading = getBlockValue(block, 'heading', lang);
  const body = getBlockValue(block, 'body', lang);
  return <section className="space-y-4">{heading && <h3 className="font-bold font-heading" style={{ fontSize: 'calc(clamp(1.5rem, 3vw, 1.875rem) * var(--section-title-scale, 1))', color: 'var(--site-heading-color, #FFFFFF)' }}>{heading}</h3>}{body && <p className="whitespace-pre-line" style={{ fontSize: 'calc(1rem * var(--body-text-scale, 1))', color: 'var(--site-body-color, rgba(255,255,255,0.78))' }}>{body}</p>}</section>;
}

export function MarqueeBlock({ block, lang }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;
  const repeatCount = items.length < 8 ? 3 : 2;
  const repeatedItems = Array.from({ length: repeatCount }, () => items).flat();
  const label = lang === 'en' ? 'OUR CLIENTS & PARTNERS' : 'НАШИ КЛИЕНТЫ И ПАРТНЕРЫ';
  const baseUrl = BACKEND_URL || window.location.origin;
  return (
    <section className="relative border-y border-white/10 bg-black/40 h-[60px] sm:h-[120px] flex items-center overflow-hidden">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="text-[clamp(1rem,3.2vw,3.5rem)] sm:text-[clamp(1.5rem,2.8vw,4rem)] font-black tracking-[0.18em] text-white/25 uppercase whitespace-nowrap text-center">{label}</span>
      </div>
      <Marquee gradient={false} speed={40} pauseOnHover>
        {repeatedItems.map((item, index) => {
          const image = item.image_url || item.url;
          if (!image) return null;
          const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
          const content = <div className="h-[50px] sm:h-[110px] p-[5px] flex items-center justify-center"><img src={imageUrl} alt="" className="h-full object-contain opacity-70" onError={(event) => setImageFallback(event)} /></div>;
          const link = item.link || item.href || '';
          return <div key={`${image}-${index}`} style={{ marginLeft: 'clamp(4px, 1.5vw, 16px)', marginRight: 'clamp(4px, 1.5vw, 16px)' }}>{link ? <a href={link} target="_blank" rel="noopener noreferrer">{content}</a> : content}</div>;
        })}
      </Marquee>
    </section>
  );
}

export function ImageBlock({ block, lang }) {
  const caption = getBlockValue(block, 'caption', lang);
  if (!block.url) return null;
  return <figure className="space-y-4"><img src={block.url} alt={caption || ''} className="w-full rounded-2xl border border-white/10" onError={(event) => setImageFallback(event)} />{caption && <figcaption className="text-white/60 text-sm">{caption}</figcaption>}</figure>;
}

export function GalleryBlock({ block }) {
  const images = Array.isArray(block.images) ? block.images : [];
  if (!images.length) return null;
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{images.map((url, index) => <img key={`${url}-${index}`} src={url || DEFAULT_GALLERY_IMAGE} alt="" className="w-full h-60 object-cover rounded-2xl border border-white/10" onError={(event) => setImageFallback(event)} />)}</div>;
}

export function VideoBlock({ block, lang }) {
  const title = getBlockValue(block, 'title', lang);
  if (!block.url) return null;
  const isYouTube = block.source === 'youtube' || /youtube\.com|youtu\.be/.test(block.url || '');
  const getYouTubeUrl = (url) => {
    if (!url) return url;
    if (url.includes('embed/')) return url;
    const shortMatch = url.match(/youtu\.be\/([^?]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    const match = url.match(/v=([^&]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    return url;
  };
  return (
    <div className="space-y-4">
      {title && <h3 className="text-2xl font-bold font-heading text-white">{title}</h3>}
      {isYouTube ? <div className="relative w-full pb-[56.25%]"><iframe title={title || 'video'} src={getYouTubeUrl(block.url)} className="absolute inset-0 w-full h-full rounded-2xl border border-white/10" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div> : <video className="w-full rounded-2xl border border-white/10" controls src={block.url} />}
    </div>
  );
}

export function FormBlock({ block, lang }) {
  if (!block.form_slug) return null;
  return <div className="glass rounded-3xl p-6 md:p-8"><DynamicForm slug={block.form_slug} lang={lang} /></div>;
}

export function SpacerBlock({ block }) {
  const size = block.size || 32;
  return <div style={{ height: size }} />;
}

export function CardsBlock({ block, lang }) {
  const title = getBlockValue(block, 'title', lang);
  const items = Array.isArray(block.items) ? block.items : [];
  const columns = Number(block.columns || block.cols || 3);
  const gridClass = columns === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';
  return (
    <section className="space-y-6">
      {title && <h3 className="text-2xl md:text-3xl font-bold font-heading text-white">{title}</h3>}
      <div className={gridClass}>
        {items.map((item, index) => {
          const itemTitle = getBlockValue(item, 'title', lang);
          const itemDesc = getBlockValue(item, 'description', lang);
          const fallbackImage = getFallbackCardImage(index);
          const imageSrc = item.image_url || fallbackImage;
          return (
            <div key={`${itemTitle}-${index}`} className="glass rounded-2xl p-5 space-y-3">
              {item.icon_url && <img src={item.icon_url} alt="" className="h-10 w-10 object-contain" onError={(event) => hideBrokenImage(event)} />}
              <img src={imageSrc} alt="" className="w-full h-40 object-cover rounded-xl" onError={(event) => setImageFallback(event, fallbackImage)} />
              {itemTitle && <h4 className="text-lg font-bold text-white">{itemTitle}</h4>}
              {itemDesc && <p className="text-white/70 text-sm">{itemDesc}</p>}
              {item.link && (item.link.startsWith('http') ? <a href={item.link} className="text-sm text-[#E11D2E] font-semibold hover:underline">{getBlockValue(item, 'link_label', lang) || (lang === 'ru' ? 'Подробнее →' : 'Learn more →')}</a> : <Link to={item.link} className="text-sm text-[#E11D2E] font-semibold hover:underline">{getBlockValue(item, 'link_label', lang) || (lang === 'ru' ? 'Подробнее →' : 'Learn more →')}</Link>)}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function StatsBlock({ block, lang }) {
  const items = Array.isArray(block.items) ? block.items : [];
  const countUpEnabled = block.countup_enabled !== false;
  const countUpDuration = Number(block.countup_duration ?? 1);
  const countUpDelay = Number(block.countup_delay ?? 0);
  const countUpDirection = block.countup_direction || 'up';
  const countUpSeparator = block.countup_separator ?? '';
  const countUpStartWhen = block.countup_start_when !== false;
  const gradientEnabled = block.gradient_enabled !== false;
  const gradientFrom = block.gradient_from || '#E11D2E';
  const gradientTo = block.gradient_to || '#FFFFFF';
  const gradientAngle = Number(block.gradient_angle ?? 90);
  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item, index) => {
        const label = getBlockValue(item, 'label', lang);
        const suffix = getBlockValue(item, 'suffix', lang) || '';
        const rawValue = item.value ?? '';
        const numericValue = Number(rawValue);
        const shouldCount = countUpEnabled && Number.isFinite(numericValue);
        const valueContent = shouldCount ? <CountUp from={0} to={numericValue} direction={countUpDirection} duration={Number.isFinite(countUpDuration) ? countUpDuration : 1} delay={Number.isFinite(countUpDelay) ? countUpDelay : 0} separator={countUpSeparator} startWhen={countUpStartWhen} className="count-up-text" /> : <span className="count-up-text">{rawValue}</span>;
        const valueWithSuffix = <>{valueContent}{suffix}</>;
        return (
          <div key={`${item.value}-${index}`} className="glass rounded-2xl p-4 text-center space-y-2">
            {gradientEnabled ? <GradientText className="text-2xl md:text-3xl font-black font-heading" from={gradientFrom} to={gradientTo} angle={gradientAngle}>{valueWithSuffix}</GradientText> : <div className="text-2xl md:text-3xl font-black font-heading" style={{ color: 'var(--site-heading-color, #FFFFFF)' }}>{valueWithSuffix}</div>}
            {label && <div style={{ color: 'var(--site-muted-color, rgba(255,255,255,0.62))', fontSize: 'calc(0.875rem * var(--small-text-scale, 1))' }}>{label}</div>}
          </div>
        );
      })}
    </section>
  );
}

export function LogoGridBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-4 items-center">
      {items.map((item, index) => {
        const image = item.image_url || item.url;
        if (!image) return null;
        const content = <img src={image || DEFAULT_GALLERY_IMAGE} alt="" className="w-full h-16 object-contain opacity-80" onError={(event) => setImageFallback(event)} />;
        return <div key={`${image}-${index}`} className="glass rounded-2xl p-4 flex items-center justify-center">{item.link ? <a href={item.link} target="_blank" rel="noreferrer">{content}</a> : content}</div>;
      })}
    </section>
  );
}

export function CtaBlock({ block, lang }) {
  const title = getBlockValue(block, 'title', lang);
  const body = getBlockValue(block, 'body', lang);
  const button = getBlockValue(block, 'button_label', lang);
  const background = block.background_image;
  const isExternal = block.button_href?.startsWith('http');
  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/10 p-8 md:p-12 bg-black/40">
      {background && <img src={background} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
      <div className="relative z-10 space-y-4">
        {title && <h3 className="font-bold font-heading" style={{ fontSize: 'calc(clamp(1.5rem, 3vw, 2.25rem) * var(--section-title-scale, 1))', color: 'var(--site-heading-color, #FFFFFF)' }}>{title}</h3>}
        {body && <p style={{ fontSize: 'calc(1rem * var(--body-text-scale, 1))', color: 'var(--site-body-color, rgba(255,255,255,0.78))' }}>{body}</p>}
        {button && block.button_href && (isExternal ? <a href={block.button_href} className="inline-flex items-center px-6 py-3" style={{ background: 'var(--site-button-bg, #E11D2E)', color: 'var(--site-button-text, #FFFFFF)', borderRadius: 'var(--site-button-radius, 9999px)', fontSize: 'var(--site-button-text-size, 0.95rem)' }}>{button}</a> : <Link to={block.button_href} className="inline-flex items-center px-6 py-3" style={{ background: 'var(--site-button-bg, #E11D2E)', color: 'var(--site-button-text, #FFFFFF)', borderRadius: 'var(--site-button-radius, 9999px)', fontSize: 'var(--site-button-text-size, 0.95rem)' }}>{button}</Link>)}
      </div>
    </section>
  );
}

export function ListBlock({ block, lang }) {
  const title = getBlockValue(block, 'title', lang);
  const items = lang === 'en' && Array.isArray(block.items_en) && block.items_en.length ? block.items_en : block.items || [];
  return <section className="space-y-4">{title && <h3 className="font-bold font-heading" style={{ fontSize: 'calc(clamp(1.5rem, 3vw, 1.875rem) * var(--section-title-scale, 1))', color: 'var(--site-heading-color, #FFFFFF)' }}>{title}</h3>}<ul className="space-y-2 list-disc list-inside" style={{ fontSize: 'calc(1rem * var(--body-text-scale, 1))', color: 'var(--site-body-color, rgba(255,255,255,0.78))' }}>{items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul></section>;
}

export function HtmlBlock({ block, lang }) {
  const html = lang === 'en' && block.html_en ? block.html_en : block.html;
  if (!html) return null;
  return <section className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
}

function getLocalizedItemValue(item, field, lang) {
  if (lang === 'en' && item?.[`${field}_en`]) {
    return item[`${field}_en`];
  }
  return item?.[field];
}

export function CollectionBlock({ block, lang }) {
  const [items, setItems] = useState([]);
  const title = getBlockValue(block, 'title', lang);
  const limit = block.limit ? Number(block.limit) : 0;

  useEffect(() => {
    const fetchItems = async () => {
      if (!block.collection) return;
      try {
        const response = await api.get(`/${block.collection}?lang=${lang}`);
        const data = Array.isArray(response.data) ? response.data : [];
        setItems(limit ? data.slice(0, limit) : data);
      } catch (error) {
        console.error('Failed to fetch collection', error);
      }
    };
    fetchItems();
  }, [block.collection, lang, limit]);

  if (!block.collection) return null;

  return (
    <section className="space-y-6">
      {title && <h3 className="text-2xl md:text-3xl font-bold font-heading text-white">{title}</h3>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => {
          const name = getLocalizedItemValue(item, 'name', lang) || getLocalizedItemValue(item, 'title', lang);
          const desc = getLocalizedItemValue(item, 'description', lang) || getLocalizedItemValue(item, 'excerpt', lang);
          const image = item.image_url || item.logo_url;
          const slug = item.slug;
          const detailPrefix = block.detail_prefix || '';
          const link = detailPrefix && slug ? `${detailPrefix}/${slug}` : null;
          const content = (
            <div className="glass rounded-2xl overflow-hidden">
              <img src={image || getFallbackCardImage(0)} alt={name || ''} className="w-full h-44 object-cover" onError={(event) => setImageFallback(event, getFallbackCardImage(0))} />
              <div className="p-4 space-y-2">
                {name && <h4 className="text-lg font-bold text-white">{name}</h4>}
                {desc && <p className="text-white/70 text-sm">{desc}</p>}
              </div>
            </div>
          );
          return <div key={item.id || slug}>{link ? <Link to={link}>{content}</Link> : content}</div>;
        })}
      </div>
    </section>
  );
}
